import React from 'react';
import { Heart, Award, Shield, Smile, PlayCircle, Tv, Info, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Mit Herz',
      description: 'Einfühlungsvermögen und Zuwendung für Ihr Tier'
    },
    {
      icon: Shield,
      title: 'Sicher',
      description: 'Professioneller und zuverlässiger Transport'
    },
    {
      icon: Smile,
      title: 'Stressfrei',
      description: 'Ihr Liebling fühlt sich bei uns wohl'
    },
    {
      icon: Award,
      title: 'Engagiert',
      description: 'Mit vollem Einsatz für Sie und Ihr Tier'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-block mb-4">
              <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                Über uns
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
                Dom's Pfotenexpress
              </span>
            </h2>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Ihr neues Tiertaxi in Hagen und Umgebung
            </h3>
            
            {/* Foto von Dom mit Hund */}
            <div className="float-right ml-6 mb-4 w-48 md:w-64">
              <img
                src="https://customer-assets.emergentagent.com/job_pettaxi-2/artifacts/0tgnd0h5_Screenshot_20260129-205246.png"
                alt="Dom mit seinem Hund"
                className="w-full h-auto rounded-2xl shadow-lg border-4 border-white"
              />
            </div>

            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Hallo! Ich bin Dom, Gründer von Dom's Pfotenexpress – Ihrem neuen Ansprechpartner für liebevollen und sicheren Tiertransport in Hagen und Umgebung.
              </p>
              <p>
                Als Tierfreund mit Herz und Verstand habe ich es mir zur Aufgabe gemacht, Haustiere stressfrei und zuverlässig zu begleiten – sei es zum Tierarzt, in die Hundepension oder auf Reisen.
              </p>
              <p>
                Auch wenn mein Tiertaxi ganz neu am Start ist, bringe ich viel Engagement, Sorgfalt und Einfühlungsvermögen mit. Ich weiß, wie wichtig es ist, dass sich Ihr Liebling unterwegs wohlfühlt – deshalb steht bei mir nicht nur Sicherheit, sondern auch Zuwendung und Geduld an erster Stelle.
              </p>
              <p className="font-semibold text-gray-900">
                Ob kleine oder große Fahrten: Ich bin für Sie da und freue mich, Sie und Ihr Tier kennenzulernen!
              </p>
            </div>

            <div className="mt-8 bg-gradient-to-r from-blue-600 to-orange-500 text-white p-6 rounded-2xl">
              <p className="text-xl font-bold text-center">
                Dom's Pfotenexpress – Mit Herz auf vier Rädern unterwegs.
              </p>
            </div>

            {/* WDR Lokalzeit Medienbeitrag */}
            <div className="mt-8" data-testid="wdr-media-feature">
              <div className="flex items-center gap-2 mb-3">
                <Tv className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-semibold uppercase tracking-wider text-blue-700">
                  Wie im Fernsehen gesehen
                </span>
              </div>
              <a
                href="https://www1.wdr.de/lokalzeit/fernsehen/dortmund/das-tiertaxi-in-hagen-100.html"
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-2xl hover:border-blue-400 transition-all duration-300"
                data-testid="wdr-video-link"
                title="Beitrag in der WDR Mediathek ansehen"
              >
                <div className="relative aspect-video bg-gradient-to-br from-blue-900 via-blue-700 to-orange-600 overflow-hidden">
                  <img
                    src="https://www1.wdr.de/mediathek/video/sendungen/lokalzeit-dortmund/sendungslogo-lokalzeit-dortmund100~_v-gseapremiumxl.jpg"
                    alt="WDR Lokalzeit Dortmund – Das Tiertaxi in Hagen"
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/95 group-hover:bg-white rounded-full p-4 shadow-2xl transition-all duration-300 group-hover:scale-110">
                      <PlayCircle className="h-12 w-12 text-blue-600" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-md shadow-md">
                    WDR
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-medium px-2.5 py-1 rounded">
                    07:21 Min.
                  </div>
                </div>
                <div className="p-5">
                  <h4 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-blue-700 transition-colors">
                    Das Tiertaxi in Hagen
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Lokalzeit aus Dortmund · 04.02.2026 · Von Olaf Tack
                  </p>
                  <p className="text-sm text-blue-600 font-semibold group-hover:underline">
                    In der WDR Mediathek ansehen →
                  </p>
                </div>
              </a>
            </div>

            {/* Kooperationspartner */}
            <div className="mt-8 bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <p className="text-sm font-semibold text-gray-600 mb-5 text-center">
                Unsere Kooperationspartner:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-6 items-start">
                {/* zookauf */}
                <div className="flex flex-col items-center">
                  <a
                    href="https://www.zookauf.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity duration-300"
                    title="zookauf - Unser Kooperationspartner"
                    data-testid="partner-zookauf-link"
                  >
                    <img
                      src="https://customer-assets.emergentagent.com/job_1e01ce1c-d940-47df-8402-fdd068fb2c01/artifacts/bu9kv6if_zookauf%20Logo%20200x100.png"
                      alt="zookauf Logo"
                      className="h-16 w-auto"
                    />
                  </a>
                  <a
                    href="https://www.zookauf.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:text-blue-600 transition-colors mt-2"
                  >
                    www.zookauf.de
                  </a>
                </div>

                {/* Silent Souls of Strays e.V. */}
                <div className="flex flex-col items-center">
                  <a
                    href="https://www.silent-souls-of-strays.de/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity duration-300"
                    title="Silent Souls of Strays e.V. - Tierschutzverein"
                    data-testid="partner-silentsouls-link"
                  >
                    <img
                      src="https://customer-assets.emergentagent.com/job_03f10bd8-00cc-48b1-87fd-4a119cdb108f/artifacts/xizgpv4r_IMG-20260608-WA0050.jpg"
                      alt="Silent Souls of Strays e.V. Logo"
                      className="h-20 w-auto"
                    />
                  </a>
                  <div className="flex items-center gap-2 mt-2">
                    <a
                      href="https://www.silent-souls-of-strays.de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      www.silent-souls-of-strays.de
                    </a>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="Mehr Informationen zu Silent Souls of Strays e.V."
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                          data-testid="partner-silentsouls-info-btn"
                        >
                          <Info className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        className="w-80 text-sm leading-relaxed text-gray-700"
                        data-testid="partner-silentsouls-info-content"
                      >
                        <p className="font-semibold text-gray-900 mb-2">
                          Silent Souls of Strays e.V.
                        </p>
                        <p>
                          Silent Souls of Strays e.V. ist ein Team aus Tierschützern,
                          mit jahrelanger Erfahrung im Auslandstierschutz. Hand in
                          Hand arbeiten wir mit Tierschützern sowohl im In- als auch
                          im Ausland zusammen. Unser Ziel ist es misshandelten,
                          herrenlosen und verletzen Tieren zu helfen und ihnen eine
                          neue Chance auf ein glückliches Leben zu bieten.
                        </p>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Vietnam Animal Aid */}
                <div className="flex flex-col items-center">
                  <a
                    href="https://www.vnanimalaid.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity duration-300"
                    title="Vietnam Animal Aid - Tierschutzorganisation"
                    data-testid="partner-vnanimalaid-link"
                  >
                    <img
                      src="https://customer-assets.emergentagent.com/job_03f10bd8-00cc-48b1-87fd-4a119cdb108f/artifacts/5661pu92_Screenshot_20260609_120453_Chrome.jpg"
                      alt="Vietnam Animal Aid Logo"
                      className="h-20 w-auto"
                    />
                  </a>
                  <div className="flex items-center gap-2 mt-2">
                    <a
                      href="https://www.vnanimalaid.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      www.vnanimalaid.org
                    </a>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="Mehr Informationen zu Vietnam Animal Aid"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                          data-testid="partner-vnanimalaid-info-btn"
                        >
                          <Info className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        className="w-80 sm:w-96 max-h-[70vh] overflow-y-auto text-sm leading-relaxed text-gray-700"
                        data-testid="partner-vnanimalaid-info-content"
                      >
                        <p className="font-semibold text-gray-900 mb-2">
                          Unsere Geschichte
                        </p>
                        <p className="mb-3">
                          Vietnam Animal Aid ist eine als gemeinnützig (nach
                          US-Recht, Status 501(c)(3)) anerkannte Organisation,
                          die in Hoi An (Zentralvietnam) eine Auffangstation
                          und einen Lebenshof für Tiere betreibt. Wir setzen
                          uns für Tierrechte ein, die frei von Speziesismus
                          sind, und kümmern uns um Katzen, Hunde, Hühner,
                          Enten sowie andere Tierarten, die lebenslange Pflege
                          benötigen. Derzeit beherbergen wir rund 35 Tiere
                          dreier verschiedener Arten.
                        </p>
                        <p className="font-semibold text-gray-900 mb-2">
                          Wie alles begann
                        </p>
                        <p className="mb-3">
                          Im Jahr 2013 gründete Catherine Besch die
                          Organisation, die zunächst unter dem Namen „Vietnam
                          Animal Welfare Organization" bekannt war. Nach einer
                          Aufspaltung der ursprünglichen Gruppe entstand
                          „Vietnam Animal Aid and Rescue – US" (VAAR). Im
                          Laufe der Zeit entwickelten wir uns von einer
                          Tierschutzgruppe zu einer Tierrechtsorganisation,
                          die sich für langfristige Lösungen einsetzt, um das
                          Leid aller Tierarten zu beenden.
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-xs">
                          <li>
                            <span className="font-semibold">2013:</span>{' '}
                            Eröffnung der ersten Auffangstation in
                            Zentralvietnam.
                          </li>
                          <li>
                            <span className="font-semibold">Kurz darauf:</span>{' '}
                            Gründung der ersten gemeinnützigen Tierklinik
                            Vietnams.
                          </li>
                          <li>
                            <span className="font-semibold">Lebenshof:</span>{' '}
                            Einrichtung des ersten Zufluchtsortes des Landes
                            für sogenannte Nutztiere.
                          </li>
                          <li>
                            <span className="font-semibold">
                              Wildtierrettung:
                            </span>{' '}
                            Zusammenarbeit mit der vietnamesischen Regierung
                            zur Versorgung von Wildtieren aus illegalem Handel
                            in der Nähe von Hue.
                          </li>
                        </ul>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Jingles & Homes - Tiernotfallkarte24 */}
                <div className="flex flex-col items-center">
                  <a
                    href="https://tiernotfallkarte24.de/#toggle-id-8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity duration-300"
                    title="Jingles & Homes - Mitglied im Tierfreunde Netzwerk Tiernotfallkarte24"
                    data-testid="partner-jinglesholmes-link"
                  >
                    <img
                      src="https://customer-assets.emergentagent.com/job_03f10bd8-00cc-48b1-87fd-4a119cdb108f/artifacts/zh75b6n9_Siegel%20%284%29.png"
                      alt="Jingles & Holmes - Tiernotfallkarte24 Siegel"
                      className="h-20 w-auto"
                    />
                  </a>
                  <div className="flex items-center gap-2 mt-2">
                    <a
                      href="https://tiernotfallkarte24.de/#toggle-id-8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      tiernotfallkarte24.de
                    </a>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="Mehr Informationen zu Jingles & Homes / Tiernotfallkarte24"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                          data-testid="partner-jinglesholmes-info-btn"
                        >
                          <Info className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        className="w-80 sm:w-96 text-sm leading-relaxed text-gray-700"
                        data-testid="partner-jinglesholmes-info-content"
                      >
                        <p className="font-semibold text-gray-900 mb-2">
                          Jingles &amp; Homes
                        </p>
                        <p className="text-xs text-gray-600 mb-3">
                          Mitglied im Tierfreunde Netzwerk Tiernotfallkarte24 –
                          hier findest du die aktuellen Info-Flyer zum Ansehen
                          oder Herunterladen:
                        </p>
                        <div className="space-y-2">
                          <a
                            href="https://customer-assets.emergentagent.com/job_03f10bd8-00cc-48b1-87fd-4a119cdb108f/artifacts/jl67i4kl_Flyer_Tiernotfallkarte24.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors group"
                            data-testid="jinglesholmes-flyer1-link"
                          >
                            <div className="w-9 h-9 rounded-md bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500 group-hover:text-white transition-colors">
                              <FileText className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 text-sm truncate">
                                Flyer Tiernotfallkarte24
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF öffnen
                              </p>
                            </div>
                          </a>
                          <a
                            href="https://customer-assets.emergentagent.com/job_03f10bd8-00cc-48b1-87fd-4a119cdb108f/artifacts/cl9zzf35_Flyer_Netzwerk.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors group"
                            data-testid="jinglesholmes-flyer2-link"
                          >
                            <div className="w-9 h-9 rounded-md bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500 group-hover:text-white transition-colors">
                              <FileText className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 text-sm truncate">
                                Flyer Netzwerk
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF öffnen
                              </p>
                            </div>
                          </a>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Fasswärme */}
                <div className="flex flex-col items-center">
                  <a
                    href="https://fasswaerme.de/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity duration-300"
                    title="Fasswärme - Wohlfühloase in Medebach"
                    data-testid="partner-fasswaerme-link"
                  >
                    <img
                      src="https://customer-assets-jai6qajn.emergentagent.net/job_03f10bd8-00cc-48b1-87fd-4a119cdb108f/artifacts/l9y5llp8_Screenshot_20260815_215923_Chrome.jpg"
                      alt="Fasswärme Logo"
                      className="h-20 w-auto"
                    />
                  </a>
                  <div className="flex items-center gap-2 mt-2">
                    <a
                      href="https://fasswaerme.de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      fasswaerme.de
                    </a>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="Mehr Informationen zu Fasswärme"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                          data-testid="partner-fasswaerme-info-btn"
                        >
                          <Info className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        className="w-80 sm:w-96 text-sm leading-relaxed text-gray-700"
                        data-testid="partner-fasswaerme-info-content"
                      >
                        <div className="bg-gradient-to-r from-orange-100 to-red-100 border border-orange-300 rounded-lg p-3 mb-3">
                          <p className="font-semibold text-orange-800 text-sm">
                            🎁 Exklusiv für unsere Kunden
                          </p>
                          <p className="text-xs text-orange-900 mt-1">
                            Auf Anfrage <strong>10 % Rabatt</strong> auf euren Aufenthalt bei Fasswärme.
                            Nennt einfach bei der Buchung, dass ihr von Dom&apos;s Pfotenexpress kommt.
                          </p>
                        </div>
                        <p className="font-semibold text-gray-900 mb-2">
                          Wohlfühloase in Medebach
                        </p>
                        <p className="mb-2">
                          Raus aus dem Alltag – rein in die Natur. Die Fasswärme am Weddelsee im
                          Sauerland bietet euch eine private Auszeit mit <strong>Fasssauna</strong>,{' '}
                          <strong>Whirlpool</strong>, gemütlicher <strong>Jurte</strong> und einem eigenen{' '}
                          <strong>See zum Baden und Angeln</strong> – ganz ohne fremde Gäste.
                        </p>
                        <p className="text-xs text-gray-600">
                          Perfekt für Kurzurlaub zu zweit, Yoga-Retreats, Geburtstage oder eine
                          Familien-Auszeit. Winterberg und Willingen liegen in unmittelbarer Nähe.
                        </p>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Values Grid */}
          <div>
            <div className="grid grid-cols-2 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-blue-400"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">{value.title}</h4>
                    <p className="text-sm text-gray-600">{value.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Image of Van */}
            <div className="mt-8 rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_585eba0e-1f9e-449c-b220-127f874d26c2/artifacts/92hbqy58_Screenshot_20251208-144336.png"
                alt="Dom's Pfotenexpress Fahrzeug"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
